/**
 * Magic-link token extraction from the URL fragment or the query string
 * (ADR-20261015-6w29; AC-7: "whether it arrived in the URL fragment or
 * the query string"). The fragment is the preferred form (TG-35 standard:
 * "browsers never send the URL fragment to the server, so the GET for
 * /redeem carries no token into Front Door or WAF logs ... or into a
 * Referer header; a query parameter would be logged at the edge") --
 * reading either is a pure, local, synchronous operation; nothing in this
 * module ever makes a network call, which is how AC-18's "the GET reads
 * no token record" holds structurally rather than by convention for the
 * fragment form. The query-string form is still logged at the edge by
 * construction (it is sent to the server on every request to this route
 * until cleared); {@link clearFragment} removes it, same as the fragment,
 * the moment the confirm POST resolves, which is the control AC-7
 * actually relies on for that form.
 */

const FRAGMENT_TOKEN_PARAM = "t";

function readTokenFromParamString(paramString: string): string | null {
  const params = new URLSearchParams(paramString);
  const token = params.get(FRAGMENT_TOKEN_PARAM);
  // An empty or whitespace-only value is treated exactly like a missing fragment, so the browser
  // never POSTs an empty token (AC-7).
  return token === null || token.trim().length === 0 ? null : token;
}

/** Reads the token from a `location.hash`-shaped string (`#t=<token>`,
 * with or without the leading `#`). Returns null if no `t` parameter is
 * present -- deliberately the same shape as "unknown token", which the
 * component resolves into the single generic not-found outcome AC-7
 * requires, rather than a distinct "malformed link" case. */
export function readTokenFromFragment(hash: string): string | null {
  const stripped = hash.startsWith("#") ? hash.slice(1) : hash;
  return readTokenFromParamString(stripped);
}

/** Reads the token from a `location.search`-shaped string (`?t=<token>`,
 * with or without the leading `?`). Same null-shape rule as
 * {@link readTokenFromFragment}. */
export function readTokenFromQuery(search: string): string | null {
  const stripped = search.startsWith("?") ? search.slice(1) : search;
  return readTokenFromParamString(stripped);
}

/** The fragment form wins when a link somehow carries both -- it is the
 * preferred form and is never logged at the edge, so preferring it over a
 * simultaneously-present query form never gives up anything the fragment
 * check alone would not already provide. Returns null only when neither
 * form carries a usable token. */
export function readTokenFromLocation(hash: string, search: string): string | null {
  return readTokenFromFragment(hash) ?? readTokenFromQuery(search);
}

//: A single, unquantified character class with no repetition of its own, exactly the same shape
//: SignInPage.tsx's own WHITESPACE_PATTERN comment documents -- this can never backtrack.
const TOKEN_ALPHABET_PATTERN = /^[A-Za-z0-9_-]+$/;

//: Generous rather than exact (ADR-20261016-f8ws): a real token is a fixed 43-character
//: base64url string (domain/tokens/entropy.py's TOKEN_BYTES=32), but this client-side check is a
//: coarse "does this even look like a link token" sanity gate, not a re-implementation of the
//: server's own validity rules -- there is no new server oracle (ADR-20261005-ce6e stands), so a
//: syntactically plausible but wrong value (an old, spent, or never-issued token) is left for the
//: confirm POST to refuse, same as before. Only a value that could never be a token this system
//: issues -- the wrong alphabet entirely, or wildly too long to be a pasted link -- is caught here.
const MAX_PLAUSIBLE_TOKEN_LENGTH = 512;

/**
 * ADR-20261016-f8ws: a coarse, client-side-only sanity check on the EXTRACTED token value (never
 * on the raw fragment/query string) -- "shape, length, alphabet". Returns false for anything
 * containing a character outside the base64url-safe set (letters, digits, `-`, `_`) -- which also
 * catches the one case a corrupted percent-escape can produce (`readTokenFromParamString`'s
 * `URLSearchParams` decode leaves an invalid escape as a literal `%`, or substitutes
 * U+FFFD for a byte sequence that is not valid UTF-8, neither of which is in that alphabet) -- or
 * longer than a browser address bar would plausibly carry for a sign-in link. Never called on an
 * empty string: `readTokenFromParamString` already resolves that to `null` upstream, a separate,
 * pre-existing "no token at all" case this function does not re-decide.
 */
export function isValidTokenFormat(token: string): boolean {
  return token.length > 0 && token.length <= MAX_PLAUSIBLE_TOKEN_LENGTH && TOKEN_ALPHABET_PATTERN.test(token);
}

/** Removes BOTH the fragment and the query string from the visible URL
 * and browser history, with no navigation and no reload (ADR-20261015-
 * 6w29: "history.replaceState strips fragment and query after any
 * outcome" -- the query string is a token-carrying form exactly like the
 * fragment now, so neither may survive a redeem attempt of either shape).
 * Deliberately NOT called on mount -- the token stays in the address bar
 * until the Continue action has run, so a reload before confirming does
 * not strand the customer with a token only this component's memory ever
 * held. */
export function clearFragment(): void {
  window.history.replaceState(null, "", window.location.pathname);
}
